import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useApi } from "@/lib/api";
import { useTrack } from "@/lib/track";
import { cn } from "@/lib/cn";

interface Props {
  itemId: string;
  isSaved: boolean;
}

/**
 * Subtle bookmark toggle for intelligence items. Matches the calm aesthetic
 * of OpportunityCard's action row — small, monochrome, only colored when
 * active.
 */
export function SaveButton({ itemId, isSaved }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();

  const toggle = useMutation({
    mutationFn: () =>
      isSaved
        ? api.del(`/intelligence/items/${itemId}/save`)
        : api.post(`/intelligence/items/${itemId}/save`),
    onSuccess: () => {
      track(isSaved ? "intelligence.unsave" : "intelligence.save");
      qc.invalidateQueries({ queryKey: ["saved-intelligence-ids"] });
      qc.invalidateQueries({ queryKey: ["intelligence-saved"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });

  const Icon = isSaved ? BookmarkCheck : Bookmark;
  return (
    <button
      type="button"
      title={isSaved ? "Saved — click to unsave" : "Save for later"}
      aria-label={isSaved ? "Saved" : "Save"}
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      className={cn(
        "p-1.5 rounded-md transition-colors",
        isSaved ? "text-gold-glow" : "text-whisper hover:text-fog",
        "hover:bg-veil/40 disabled:opacity-40 disabled:cursor-not-allowed",
      )}
    >
      <Icon className="size-4" strokeWidth={1.5} />
    </button>
  );
}
